'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';
import ArticleCard from './ArticleCard';
import SearchBar from './SearchBar';
import CategoryFilter from './CategoryFilter';
import FeaturedArticle from './FeaturedArticle';

interface Article {
  id: string;
  title: string;
  excerpt: string;
  content: string;
  category: string;
  author: string;
  authorRole: string;
  publishDate: string;
  readTime: string;
  image: string;
  alt: string;
  tags: string[];
  featured: boolean;
}

const BlogArticlesInteractive = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const categories = [
  { id: 'all', label: 'All Articles', icon: 'NewspaperIcon' },
  { id: 'travel-tips', label: 'Travel Tips', icon: 'LightBulbIcon' },
  { id: 'destinations', label: 'Destinations', icon: 'GlobeAltIcon' },
  { id: 'ai-innovation', label: 'AI & Innovation', icon: 'SparklesIcon' },
  { id: 'industry-news', label: 'Industry News', icon: 'MegaphoneIcon' }];


  const mockArticles: Article[] = [
  {
    id: 'ART001',
    title: 'How AI is Revolutionizing Travel Planning in 2024',
    excerpt: 'Discover how artificial intelligence is transforming the way we plan, book, and experience travel with personalized recommendations and automated services.',
    content: 'Full article content...',
    category: 'ai-innovation',
    author: 'Sarah Johnson',
    authorRole: 'AI Travel Expert',
    publishDate: '2024-02-15',
    readTime: '8 min read',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1bbf2c10a-1772297240479.png",
    alt: 'Modern laptop displaying AI travel planning interface with colorful data visualizations and flight routes',
    tags: ['AI', 'Innovation', 'Technology'],
    featured: true
  },
  {
    id: 'ART002',
    title: '10 Hidden Gems in Europe You Must Visit This Summer',
    excerpt: 'Escape the tourist crowds and explore these breathtaking European destinations that offer authentic experiences and stunning landscapes.',
    content: 'Full article content...',
    category: 'destinations',
    author: 'Michael Chen',
    authorRole: 'Travel Writer',
    publishDate: '2024-02-12',
    readTime: '6 min read',
    image: "https://images.unsplash.com/photo-1688559580179-1430319bee58",
    alt: 'Picturesque European village with colorful houses along cobblestone street and mountain backdrop',
    tags: ['Europe', 'Destinations', 'Summer Travel'],
    featured: false
  },
  {
    id: 'ART003',
    title: 'Ultimate Guide to Budget Travel: Save Money Without Sacrificing Experience',
    excerpt: 'Learn expert strategies to travel the world on a budget, from finding cheap flights to discovering affordable accommodations and local experiences.',
    content: 'Full article content...',
    category: 'travel-tips',
    author: 'Emma Williams',
    authorRole: 'Budget Travel Specialist',
    publishDate: '2024-02-10',
    readTime: '10 min read',
    image: "https://images.unsplash.com/photo-1549291447-9847678c8a46",
    alt: 'Backpacker with map standing on mountain peak overlooking scenic valley at sunrise',
    tags: ['Budget Travel', 'Tips', 'Money Saving'],
    featured: false
  },
  {
    id: 'ART004',
    title: 'The Future of Sustainable Aviation: What Travelers Need to Know',
    excerpt: 'Explore the latest developments in sustainable aviation technology and how the airline industry is working towards carbon-neutral flights.',
    content: 'Full article content...',
    category: 'industry-news',
    author: 'David Martinez',
    authorRole: 'Aviation Analyst',
    publishDate: '2024-02-08',
    readTime: '7 min read',
    image: "https://images.unsplash.com/photo-1685677199641-feb5cef94b76",
    alt: 'Modern aircraft flying through clear blue sky with white clouds',
    tags: ['Sustainability', 'Aviation', 'Environment'],
    featured: false
  },
  {
    id: 'ART005',
    title: 'Top 5 Travel Apps Every Smart Traveler Should Use',
    excerpt: 'Maximize your travel experience with these essential mobile apps that help with navigation, translation, booking, and local recommendations.',
    content: 'Full article content...',
    category: 'travel-tips',
    author: 'Lisa Anderson',
    authorRole: 'Tech Travel Blogger',
    publishDate: '2024-02-05',
    readTime: '5 min read',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_10830857b-1767003545018.png",
    alt: 'Smartphone displaying travel apps on screen with passport and boarding pass in background',
    tags: ['Apps', 'Technology', 'Travel Tips'],
    featured: false
  },
  {
    id: 'ART006',
    title: 'Exploring Asia: A Complete Guide to Southeast Asian Adventures',
    excerpt: 'From Thailand beaches to Vietnam street food, discover the ultimate itinerary for exploring Southeast Asia diverse cultures and landscapes.',
    content: 'Full article content...',
    category: 'destinations',
    author: 'James Lee',
    authorRole: 'Asia Travel Expert',
    publishDate: '2024-02-03',
    readTime: '12 min read',
    image: "https://images.unsplash.com/photo-1688060069148-ce40c78a228b",
    alt: 'Traditional Asian temple with golden spires surrounded by lush tropical jungle',
    tags: ['Asia', 'Destinations', 'Adventure'],
    featured: false
  }];


  const featuredArticle = mockArticles.find((article) => article.featured);
  const regularArticles = mockArticles.filter((article) => !article.featured);

  const filteredArticles = regularArticles.filter((article) => {
    const matchesSearch = article.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    article.excerpt.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || article.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  if (!isHydrated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-surface via-white to-primary/5 pt-24 pb-16">
        <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="animate-pulse space-y-8">
            <div className="h-12 bg-gray-200 rounded-lg w-1/3"></div>
            <div className="h-96 bg-gray-200 rounded-lg"></div>
          </div>
        </div>
      </div>);

  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-surface via-white to-primary/5 pt-24 pb-16">
      <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="font-heading text-4xl md:text-5xl font-black text-foreground mb-2">
            Blog & Articles
          </h1>
          <p className="text-lg text-muted-foreground">
            Travel insights, tips, and industry news from our expert team
          </p>
        </div>

        {/* Featured Article */}
        {featuredArticle &&
        <div className="mb-12">
            <FeaturedArticle article={featuredArticle} />
          </div>
        }

        {/* Search and Filters */}
        <div className="mb-8 space-y-4">
          <SearchBar searchQuery={searchQuery} setSearchQuery={setSearchQuery} />
          <div className="flex flex-wrap items-center justify-between gap-4">
            <CategoryFilter
              categories={categories}
              selectedCategory={selectedCategory}
              setSelectedCategory={setSelectedCategory} />
            
            <div className="flex items-center gap-2">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded-lg transition-all duration-200 ${
                viewMode === 'grid' ? 'bg-primary text-white' : 'bg-surface text-muted-foreground hover:bg-primary/10'}`
                }>
                
                <Icon name="Squares2X2Icon" size={20} />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded-lg transition-all duration-200 ${
                viewMode === 'list' ? 'bg-primary text-white' : 'bg-surface text-muted-foreground hover:bg-primary/10'}`
                }>
                
                <Icon name="ListBulletIcon" size={20} />
              </button>
            </div>
          </div>
        </div>

        {/* Articles Grid/List */}
        {filteredArticles.length === 0 ?
        <div className="text-center py-16 bg-white rounded-xl border border-border">
            <Icon name="MagnifyingGlassIcon" size={64} className="mx-auto mb-4 text-muted-foreground opacity-50" />
            <h3 className="text-xl font-black text-foreground mb-2">No Articles Found</h3>
            <p className="text-muted-foreground">Try adjusting your search or filters</p>
          </div> :

        <div className={viewMode === 'grid' ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6' : 'space-y-6'}>
            {filteredArticles.map((article) =>
          <ArticleCard key={article.id} article={article} viewMode={viewMode} />
          )}
          </div>
        }
      </div>
    </div>);

};

export default BlogArticlesInteractive;